import React, { useEffect, useMemo } from 'react';
import {
  Button, message, Form,
} from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { SaveOutlined } from '@ant-design/icons';
import { TariffForm, TariffFormValues, TariffPeriodFormValues } from '../Form';
import {
  Tariff,
  useTariffGetById,
  useTariffUpdate,
  type TariffUpsertParams, Periods,
} from '../../../../hooks/api/tariffs';
import SinglePageContainer from '../../../Common/SinglePageContainer';
import DeleteTariffButton from '../actions';
import { useMessageError } from '../../../../hooks/common';
import { toBerlinStartOfDayISO, toBerlinEndOfDayISO, fromBerlinISO } from '../../../../utils/date';
import { useAuth } from '../../../../store/auth';
import { UserRole } from '../../../../enums/user';

export default function TariffEditPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm<TariffFormValues>();
  const { user } = useAuth();
  const isReadOnly = user?.role === UserRole.MITARBEITER;

  const tariffGet = useTariffGetById(id);
  const tariffUpdate = useTariffUpdate(id);

  const initialValues: TariffFormValues | undefined = useMemo(() => {
    const data = tariffGet?.data as Tariff | undefined;

    if (!data) return undefined;

    const {
      tariffId,
      name,
      isActive,
      isPriceOverviewVisible,
      basicPrice,
      basicPricePeriod,
      energyPrice,
      energyPricePeriod,
      numberOfMonths,
      brevoListId,
      invitationPageUrl,
      priceOverview,
      tooltips,
      periods,
    } = data;

    return {
      tariffId,
      name,
      isActive,
      isPriceOverviewVisible,
      basicPrice,
      basicPricePeriod,
      energyPrice,
      energyPricePeriod,
      numberOfMonths,
      brevoListId,
      invitationPageUrl,
      priceOverview,
      tooltips,
      periods: periods?.map(({
        endDate, startDate, ...rest
      }) => ({
        ...rest,
        startDate: startDate ? fromBerlinISO(startDate) : null,
        endDate: endDate ? fromBerlinISO(endDate) : null,
      })) || [],
    };
  }, [tariffGet?.data]);

  const handleSubmit = async (values: TariffFormValues) => {
    if (!id) {
      message.error('Tariff ID is missing');

      return;
    }

    const payload: TariffUpsertParams = {
      ...values,
      name: values.name.trim(),
      tariffId: Number(values.tariffId),
      isActive: values.isActive ?? true,
      periods: (values.periods ?? []).map(({ startDate, endDate, ...rest }) => {
        const data: Partial<TariffPeriodFormValues> = {
          ...rest,
        };

        if (startDate) {
          data.startDate = toBerlinStartOfDayISO(startDate);
        }

        if (endDate) {
          data.endDate = toBerlinEndOfDayISO(endDate);
        }

        return data as Periods;
      }),
    };

    try {
      await tariffUpdate.fetch(payload).then((res) => {
        if (res?.id) {
          message.success('Tariff has been updated successfully');
        } else {
          message.error('Something went wrong!');
        }
      });

      // navigate('/tariffs');
    } catch (error: any) {
      message.error(error?.message);
    }
  };

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues);
    }
  }, [initialValues, form]);

  useMessageError([tariffGet]);

  return (
    <SinglePageContainer
      cardLoading={tariffGet.loading}
      title={`${isReadOnly ? 'View' : 'Edit'} Tariff ${tariffGet.data?.name || ''}`}
      extra={(
        <>
          {tariffGet.data && !isReadOnly && (
            <DeleteTariffButton
              record={{ ...tariffGet.data, key: tariffGet.data.id }}
              afterSuccess={() => (navigate('/tariffs'))}
            />
          )}
          {!isReadOnly && (
            <Button
              onClick={() => form.submit()}
              icon={<SaveOutlined />}
              loading={tariffUpdate.loading}
            >
              Save
            </Button>
          )}
        </>
      )}
    >
      <div style={{ maxWidth: 760 }}>
        <TariffForm
          form={form}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          disabled={isReadOnly}
        />
      </div>
    </SinglePageContainer>
  );
}
